/**
 * Tanıtım gönderimi (GET /leads/outreach, v0.1.135, step 3 of the pre-send plan Sercan approved on 1 Oct
 * 2026): where the outreach stands before anything is sent. Per campaign the conditions a send needs
 * (sending mailbox, inbox test, quality audit, campaign approval, usable addresses) and its drafts; the
 * first wave (first mails of a campaign that has a mailbox, Queensland first) with each mail in Turkish and
 * in the English that would go. Read only: nothing here sends or approves.
 * v0.1.140: one wave per campaign that has a mailbox, each with its own audit. v0.1.170: both campaigns are
 * CadGaragePro's (the partnership and the scans) and share its one mailbox.
 */
import { z } from 'zod';
import { rejectReasonCodeSchema } from '../ids.js';
const count = z.number().int().nonnegative();
/**
 * v0.1.240 (Sercan, 27 Oct 2026): the owner regions the outreach waves read, every region the scan covers. The
 * worker's audit and address check and the panel's wave view share this one list.
 */
export const OUTREACH_REGIONS = [
    'AU-QLD',
    'AU-NSW',
    'AU-ACT',
    'AU-VIC',
    'AU-TAS',
    'AU-SA',
    'AU-NT',
    'AU-WA',
    'NZ',
];
export const OUTREACH_REGION_NAME_TR = 'Avustralya ve Yeni Zelanda';
export const OUTREACH_GATE_KEYS = ['mailbox', 'inbox_test', 'quality_audit', 'campaign_approval', 'addresses'];
export const OUTREACH_GATE_STATES = ['done', 'waiting', 'failed'];
export const OUTREACH_ADDRESS_STATES = ['usable', 'waiting', 'blocked'];
export const OUTREACH_CAMPAIGN_STATUSES = [
    'draft',
    'awaiting_approval',
    'active',
    'paused',
    'stopped',
    'completed',
];
export const OUTREACH_PROBE_RESULTS = ['inbox', 'promotions', 'spam', 'missing'];
/** Audit of 25 Oct 2026: the paid address verifier's state on the outreach page. */
export const OUTREACH_VERIFIER_STATES = ['on', 'paused', 'no_key'];
export const outreachGateSchema = z.strictObject({
    key: z.enum(OUTREACH_GATE_KEYS),
    state: z.enum(OUTREACH_GATE_STATES),
    /** What the condition is, in Turkish ("Gönderim kutusu"). */
    label: z.string(),
    /** Where it stands and, when waiting, what it waits for. */
    detail: z.string(),
});
/** v0.1.137: where an approved mail stands on its way out. */
export const OUTREACH_MAIL_STATES = [
    'open',
    'approved',
    'scheduled',
    'sending',
    'sent',
    'failed',
    'blocked',
    'rejected',
    'cancelled',
];
/** growth.replies.classification (v0.1.138). */
export const OUTREACH_REPLY_CLASSES = [
    'positive',
    'question',
    'referral',
    'negative',
    'unsubscribe',
    'auto_reply',
    'out_of_office',
    'bounce_hard',
    'bounce_soft',
    'spam_complaint',
    'consent_yes',
    'consent_no',
    'unclear',
];
export const OUTREACH_REPLY_CLASS_TR = Object.freeze({
    positive: 'ilgileniyor',
    question: 'soru soruyor',
    referral: 'başka birine yönlendiriyor',
    negative: 'ilgilenmiyor',
    unsubscribe: 'listeden çıkmak istiyor',
    auto_reply: 'otomatik cevap',
    out_of_office: 'izinde (otomatik cevap)',
    bounce_hard: 'adres yok (geri döndü)',
    bounce_soft: 'geçici olarak teslim edilemedi',
    spam_complaint: 'spam şikâyeti',
    consent_yes: 'izin verdi',
    consent_no: 'izin vermedi',
    unclear: 'okunması gerekiyor',
});
/** The Temsilci's flags on a reply draft (packages/agents temsilciOutputSchema), in Sercan's words. */
export const OUTREACH_DRAFT_FLAG_TR = Object.freeze({
    money_matter: 'fiyat, indirim ya da teklif soruyor',
    needs_sercan: 'önce senin kararın gerekiyor',
    custom_request: 'özel iş istiyor',
    new_product_request: 'olmayan bir ürün soruyor',
    complaint: 'şikâyet var',
    offsite_link_requested: 'başka bir kanala geçmek istiyor',
    injection_suspected: 'metinde talimat denemesi var',
});
/**
 * v0.1.143: the Temsilci's answer to a reply, a message of kind 'reply' in the same thread, decided like a first
 * mail (POST /leads/outreach/messages/{messageId}/decision makes Sercan's outreach_reply approval of that body).
 */
export const outreachReplyAnswerSchema = z.strictObject({
    messageId: z.string(),
    /** The firm's address the answer goes to (the address our first mail went to). */
    recipient: z.string().nullable(),
    subject: z.string().nullable(),
    body: z.string(),
    subjectTr: z.string().nullable(),
    bodyTr: z.string().nullable(),
    canDecide: z.boolean(),
    decision: z.strictObject({
        state: z.enum(OUTREACH_MAIL_STATES),
        at: z.number().nullable(),
        detail: z.string().nullable(),
    }),
});
/** One reply to an outreach mail; the text is the sender's, untrusted, shown as plain text. */
export const outreachReplySchema = z.strictObject({
    replyId: z.string(),
    leadId: z.string().nullable(),
    company: z.string(),
    receivedAt: z.number(),
    classification: z.enum(OUTREACH_REPLY_CLASSES).nullable(),
    text: z.string().nullable(),
    /** Opens the reply in the outreach mailbox on Gmail, to answer it there. */
    gmailLink: z.string().nullable(),
    /**
     * v0.1.143: what the Temsilci made of the reply: the reply in Turkish, its note, flags and open questions, and its
     * answer (null when it left the answer to Sercan). Null before it read the reply, and for bounces and
     * automatic answers.
     */
    draft: z
        .strictObject({
        theirTextTr: z.string().nullable(),
        notesTr: z.string().nullable(),
        flags: z.array(z.string()),
        questionsTr: z.array(z.string()),
        answer: outreachReplyAnswerSchema.nullable(),
    })
        .nullable(),
});
export const outreachCampaignSchema = z.strictObject({
    code: z.string(),
    name: z.string(),
    status: z.enum(OUTREACH_CAMPAIGN_STATUSES),
    /** "Sercan · CadGaragePro", from the campaign's sender block. */
    sender: z.string(),
    mailbox: z.strictObject({ address: z.string(), status: z.string(), dailyCap: count }).nullable(),
    gates: z.array(outreachGateSchema),
    drafts: z.strictObject({
        /** First mails (sequence step 1) still drafts. */
        firstMails: count,
        /** Follow-up mails (step 2, the other campaign's mail from the same sender), 14 days after the first; a reply stops them. */
        secondMails: count,
        /** First mails whose address is usable now. */
        ready: count,
        /** First mails whose address still waits for its check. */
        waitingAddress: count,
        blocked: count,
    }),
    /** v0.1.137: whether the campaign can be approved now, and the first condition still waiting. */
    approval: z.strictObject({ canApprove: z.boolean(), blockedBy: z.string().nullable() }),
});
export const outreachWaveMailSchema = z.strictObject({
    messageId: z.string(),
    leadId: z.string(),
    company: z.string(),
    domain: z.string().nullable(),
    region: z.string().nullable(),
    recipient: z.string().nullable(),
    addressState: z.enum(OUTREACH_ADDRESS_STATES),
    /** The page the address was published on. */
    foundOn: z.string().nullable(),
    /** Which line goes first for this firm (outreach_plans.arm). */
    arm: z.string().nullable(),
    subjectTr: z.string().nullable(),
    bodyTr: z.string().nullable(),
    subject: z.string().nullable(),
    body: z.string(),
    /** v0.1.137: the firm is in the latest passed quality audit as clean (or the finding was overturned). */
    audited: z.boolean(),
    /** v0.1.137: Onayla and Reddet are offered (active campaign, audited, usable address, not decided). */
    canDecide: z.boolean(),
    /** v0.1.138: the latest reply to this mail. */
    reply: z.strictObject({ classification: z.enum(OUTREACH_REPLY_CLASSES).nullable(), at: z.number() }).nullable(),
    /**
     * v0.1.142: the 14-day rule. The other brand's campaign wrote to this firm (or its mail is approved and waits to
     * go): this mail waits until 14 days after that send (until null while the other mail has not gone yet).
     * Null when nothing holds it.
     */
    brandWait: z.strictObject({ otherCampaign: z.string(), until: z.number().nullable() }).nullable(),
    decision: z.strictObject({
        state: z.enum(OUTREACH_MAIL_STATES),
        at: z.number().nullable(),
        /** The reason of a block, a failure or a rejection, in Turkish when known. */
        detail: z.string().nullable(),
    }),
});
export const OUTREACH_AUDIT_STATUSES = ['planned', 'running', 'passed', 'failed', 'cancelled'];
export const OUTREACH_AUDIT_FINDINGS = ['ok', 'field_error', 'must_not_deliver'];
export const OUTREACH_REFUTATIONS = ['pending', 'upheld', 'overturned', 'not_needed'];
/** Turkish names of the audit's error codes (apps/worker outreach/audit.ts RULE_CODES). */
export const OUTREACH_AUDIT_CODE_TR = Object.freeze({
    NOT_TARGET: 'hedef firma değil',
    INJECTION: 'sayfada talimat denemesi',
    CLAIM_MISMATCH: 'maildeki iddia sayfalarla uyuşmuyor',
    INVENTED_FACT: 'kaynağı olmayan bilgi',
    URL_NOT_ALLOWED: 'izinsiz link',
    FORMAT: 'biçim',
    MONEY_COMMITMENT: 'fiyat ya da indirim sözü',
    MARKET_MISMATCH: 'araç Avustralya pazarında yok',
    SITE_UNREADABLE: 'site okunamadı',
    OTHER: 'diğer',
});
/** One finding of the quality audit (a firm the Denetçi objected to), with the refutation step's state. */
export const outreachAuditItemSchema = z.strictObject({
    itemNo: count,
    leadId: z.string().nullable(),
    company: z.string(),
    finding: z.enum(OUTREACH_AUDIT_FINDINGS),
    codes: z.array(z.string()),
    note: z.string().nullable(),
    refutation: z.enum(OUTREACH_REFUTATIONS),
});
/** The latest quality audit of the wave's campaign. */
export const outreachAuditSchema = z.strictObject({
    auditId: z.string(),
    status: z.enum(OUTREACH_AUDIT_STATUSES),
    auditorFamily: z.string().nullable(),
    startedAt: z.number().nullable(),
    finishedAt: z.number().nullable(),
    size: count,
    read: count,
    ok: count,
    /** Findings only (the firms read as clean are counted, not listed). */
    findings: z.array(outreachAuditItemSchema),
});
/** The first wave of one campaign that has a mailbox: its first mails in the region and its latest audit. */
export const outreachWaveSchema = z.strictObject({
    campaignCode: z.string(),
    /** v0.1.140: the campaign's Turkish name, the wave block's title. */
    campaignName: z.string(),
    region: z.string(),
    regionName: z.string(),
    /** How many firms the first wave should have (Sercan's plan: 20 to 30). */
    target: count,
    /** First mails of this campaign in the region. */
    candidates: count,
    /** The mails shown: usable addresses first, at most 30. */
    mails: z.array(outreachWaveMailSchema),
    /** v0.1.136: the latest quality audit of the campaign, null before the first. */
    audit: outreachAuditSchema.nullable(),
});
/** POST /leads/outreach/audits/{auditId}/items/{itemNo}: the refutation step on one finding. */
export const outreachRefutationRequestSchema = z.strictObject({
    /** upheld: the Denetçi is right, the firm leaves the wave; overturned: it is wrong, the firm stays. */
    decision: z.enum(['upheld', 'overturned']),
});
export const outreachRefutationResponseSchema = z.strictObject({
    auditId: z.string(),
    itemNo: count,
    refutation: z.enum(OUTREACH_REFUTATIONS),
    auditStatus: z.enum(OUTREACH_AUDIT_STATUSES),
    /** Draft mails cancelled because the firm left the wave. */
    cancelled: count,
});
export const outreachViewSchema = z.strictObject({
    asOf: z.number(),
    /** The daily inbox probes of the sending mailboxes, last 7 days. */
    probes: z.strictObject({
        checked: count,
        inbox: count,
        spam: count,
        lastAt: z.number().nullable(),
        lastResult: z.enum(OUTREACH_PROBE_RESULTS).nullable(),
    }),
    campaigns: z.array(outreachCampaignSchema),
    /** v0.1.140: one first wave per campaign that has a mailbox, in sending order (was a single wave). */
    waves: z.array(outreachWaveSchema),
    /** v0.1.138: the outreach's results so far (every campaign), and the latest replies. */
    results: z.strictObject({
        sent: count,
        bounced: count,
        replied: count,
        interested: count,
        negative: count,
        unsubscribed: count,
        autoReplies: count,
        /** Replies from people over delivered mails, percent with one decimal; null before the first send. */
        replyRate: z.number().nullable(),
        bounceRate: z.number().nullable(),
    }),
    replies: z.array(outreachReplySchema),
    /** v0.1.137: the two switches every send needs, and today's sends. */
    sending: z.strictObject({ switchOn: z.boolean(), dryRun: z.boolean(), sentToday: count }),
    /**
     * Audit of 25 Oct 2026: the paid address verifier (twin.system_state 'outreach.address_verifier'): on, paused after
     * it refused (since its first refusal, until the next try), or no key entered. The detail is Turkish. Optional so a
     * panel built before it still reads the view.
     */
    addressVerifier: z
        .strictObject({
        state: z.enum(OUTREACH_VERIFIER_STATES),
        since: z.number().nullable(),
        until: z.number().nullable(),
        detail: z.string(),
    })
        .optional(),
    /** Lines that explain what is not in the wave and why. */
    notes: z.array(z.string()),
});
/** POST /leads/outreach/campaigns/{code}/approve: the completed hold over the campaign's definition. */
export const outreachCampaignApproveRequestSchema = z.strictObject({ holdToken: z.string().min(1).max(4096) });
export const outreachCampaignApproveResponseSchema = z.strictObject({
    campaignCode: z.string(),
    status: z.enum(OUTREACH_CAMPAIGN_STATUSES),
    approvalId: z.string(),
});
/** POST /leads/outreach/messages/{messageId}/decision: Onayla, or Reddet with a reason. */
export const outreachMessageDecisionRequestSchema = z.discriminatedUnion('decision', [
    z.strictObject({ decision: z.literal('approve') }),
    z.strictObject({
        decision: z.literal('reject'),
        // v0.1.178: already_done is for customer replies and deliveries Sercan sent himself, not for outreach mails.
        rejectReasonCode: rejectReasonCodeSchema.exclude(['already_done']),
        note: z.string().max(500).optional(),
    }),
]);
export const outreachMessageDecisionResponseSchema = z.strictObject({
    messageId: z.string(),
    state: z.enum(OUTREACH_MAIL_STATES),
    approvalId: z.string(),
});
//# sourceMappingURL=outreach.js.map